<script setup lang="ts">
import Button from "primevue/button";
import ConfirmDialog from "primevue/confirmdialog";
import ProgressSpinner from "primevue/progressspinner";
import Toast from "primevue/toast";
import { useToast } from "primevue/usetoast";
import { RouterLink, RouterView, useRouter } from "vue-router";

import SessionExpiredDialog from "./components/SessionExpiredDialog.vue";
import { useAsyncAction } from "./composables/useAsyncAction";
import { useSession } from "./composables/useSession";

const { user, checking, isSignedIn, signOut } = useSession();
const router = useRouter();
const toast = useToast();

const {
  busy: leaving,
  errors: leaveErrors,
  run: runSignOut,
} = useAsyncAction(signOut);

async function leave(): Promise<void> {
  await runSignOut();

  if (leaveErrors.value.length > 0) {
    toast.add({
      severity: "error",
      summary: "Abmelden hat nicht geklappt",
      detail: leaveErrors.value[0],
      life: 6000,
    });
    return;
  }

  await router.push({ name: "anmelden" });
}
</script>

<template>
  <div class="admin-shell">
    <header v-if="isSignedIn" class="admin-header">
      <span class="admin-brand">Tubetänzig – Verwaltung</span>

      <!--
        The order is the order of the work, not of the routes: the start page
        first because it is what a visitor sees first, then the four kinds of
        content she keeps up to date, and behind those the three entries she
        opens rarely. Renaming or repointing one of them is a different change
        than moving it, so client/src/App.spec.ts pins label and target
        together.
      -->
      <nav class="admin-nav" aria-label="Hauptnavigation">
        <RouterLink :to="{ name: 'startseite' }">Startseite</RouterLink>
        <RouterLink :to="{ name: 'auftritte' }">Auftritte</RouterLink>
        <RouterLink :to="{ name: 'bilder' }">Bilder</RouterLink>
        <RouterLink :to="{ name: 'videos' }">Videos</RouterLink>
        <RouterLink :to="{ name: 'musik' }">Musik</RouterLink>
        <RouterLink :to="{ name: 'einstellungen' }">Einstellungen</RouterLink>
        <RouterLink :to="{ name: 'passwort' }">Passwort</RouterLink>
        <RouterLink :to="{ name: 'fehlerprotokoll' }">Fehler</RouterLink>
      </nav>

      <div class="admin-user">
        <span class="muted">{{ user?.username }}</span>
        <Button
          label="Abmelden"
          icon="pi pi-sign-out"
          severity="secondary"
          size="small"
          :loading="leaving"
          @click="leave"
        />
      </div>
    </header>

    <main class="admin-main">
      <div v-if="checking" class="centered-card" style="text-align: center">
        <ProgressSpinner style="width: 3rem; height: 3rem" />
        <p class="muted">Einen Moment …</p>
      </div>
      <RouterView v-else />
    </main>

    <Toast position="top-right" />
    <ConfirmDialog />
    <SessionExpiredDialog />
  </div>
</template>
